import { useCallback, useEffect, useState } from "react";
import {
  isProjectFavorite,
  readFavoriteReadyMap,
  retryFavoriteProject,
  toggleProjectFavorite,
} from "../helpers/projectFavorites";

/**
 * Favorite state for ONE project (used by the Project page). The Projects hub
 * keeps its own list-wide state; both read the same org-scoped record and
 * follow the same `crewpilot-project-favorites` event.
 */
export function useProjectFavorite(actor, projectId) {
  const [state, setState] = useState({ favorite: false, ready: "" });

  const userId = actor?.supabase_user_id || "";
  const orgId = actor?.organization_id || "";

  useEffect(() => {
    // Only set state when a value really changed: callers may pass a fresh
    // actor object every render.
    const apply = (next) =>
      setState((current) =>
        current.favorite === next.favorite && current.ready === next.ready ? current : next,
      );
    if (!userId || !projectId) {
      apply({ favorite: false, ready: "" });
      return undefined;
    }
    const read = () =>
      apply({
        favorite: isProjectFavorite(actor, projectId),
        ready: readFavoriteReadyMap(actor)[projectId] || "",
      });
    read();
    window.addEventListener("crewpilot-project-favorites", read);
    return () => window.removeEventListener("crewpilot-project-favorites", read);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, orgId, projectId]);

  const toggle = useCallback(
    () => (projectId ? toggleProjectFavorite(actor, projectId) : null),
    [actor, projectId],
  );
  const retry = useCallback(
    () => (projectId ? retryFavoriteProject(actor, projectId) : null),
    [actor, projectId],
  );

  return { ...state, toggle, retry };
}
